import React, { createContext, useContext, useEffect, useEffectEvent, useMemo, useReducer } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { useTranslation } from '@hooks/index';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

type SMActiveReviewContextState =
    | {
          reviewRecordId: number;
          sessionId: string;
      }
    | {
          reviewRecordId: undefined;
          sessionId: undefined;
      };

type SMActiveReviewContextAction =
    | {
          type: 'SET_ACTIVE_REVIEW';
          payload: {
              reviewRecordId: number;
              sessionId: string;
          };
      }
    | {
          type: 'CLEAR_ACTIVE_REVIEW';
      };

type SMActiveReviewContextType = {
    state: SMActiveReviewContextState;
    dispatch: React.ActionDispatch<[action: SMActiveReviewContextAction]>;
};

function SMActiveReviewContextReducer(
    state: SMActiveReviewContextState,
    action: SMActiveReviewContextAction,
): SMActiveReviewContextState {
    switch (action.type) {
        case 'SET_ACTIVE_REVIEW':
            return {
                ...structuredClone(state),
                reviewRecordId: action.payload.reviewRecordId,
                sessionId: action.payload.sessionId,
            };
        case 'CLEAR_ACTIVE_REVIEW':
            return {
                ...structuredClone(state),
                reviewRecordId: undefined,
                sessionId: undefined,
            };

        default:
            return structuredClone(state);
    }
}

export const SMActiveReviewContext = createContext<SMActiveReviewContextType | null>(null);

export const useSMActiveReviewContext = () => {
    const context = useContext(SMActiveReviewContext);

    if (context == null) {
        throw new Error('SMActiveReviewContext used outside of SMActiveReviewProvider!');
    }

    return context;
};

type Props = {
    studyRef: string;
    children?: React.ReactNode | React.ReactNode[];
};
export const SMActiveReviewProvider = ({ studyRef, children }: Props) => {
    const { t } = useTranslation('sm', { keyPrefix: 'monitoringToolkit.activeReviewTab.context' });

    const [state, dispatch] = useReducer(SMActiveReviewContextReducer, {
        reviewRecordId: undefined,
        sessionId: undefined,
    });

    const getReviewQuery = SubjectMonitoringReviewAPI.useGetByRecordIdQuery(studyRef, state.reviewRecordId);
    const review = getReviewQuery.data;

    const { dispatch: pageToolbarDispatch } = usePageToolbar();

    const value = useMemo(
        () => ({
            state,
            dispatch,
        }),
        [state, dispatch],
    );

    // When active review mode is entered/exited, add/remove badge to/from toolbar
    useEffect(() => {
        const badgeText = review != null ? `${t('reviewOpen')}: ${review.type} ${review.title}` : undefined;

        pageToolbarDispatch({
            type: 'setToolbarBadges',
            toolbarBadges:
                value.state.reviewRecordId != null && badgeText != null
                    ? [
                          {
                              key: 'active-review-mode-badge',
                              variant: 'secondary',
                              children: (
                                  <div>
                                      <FontAwesomeIcon icon={['far', 'file']} style={{ marginRight: 6 }} />
                                      <span>{badgeText}</span>
                                  </div>
                              ),
                              tooltip: badgeText,
                              maxWidth: '300px',
                          },
                      ]
                    : undefined,
        });
    }, [value.state.reviewRecordId, pageToolbarDispatch, review, t]);

    // When context unmounts (user goes to a page without the SM toolkit), remove badge from toolbar
    // effectEvent since we only ever want this to run on unmount, not when `pageToolbarDispatch` changes
    const pageToolbarDispatchEvent = useEffectEvent(pageToolbarDispatch);
    useEffect(() => {
        return () => {
            pageToolbarDispatchEvent({
                type: 'setToolbarBadges',
                toolbarBadges: undefined,
            });
        };
    }, []);

    return <SMActiveReviewContext value={value}>{children}</SMActiveReviewContext>;
};
